import { serviceConstants } from '../../../services/common/constants';
import { getUserAuthToken } from '../../../services/common/util';
import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import toastr from 'toastr';
import { getReportSettings, saveReportSettings, setDefaultLab, previewReport } from '../../../services/clinicPortalServices/reportSettingsService';

import PageHeader from '../../common/PageHeader';
const DEFAULTS = { report_title: '', accent_color: '#17a2b8', show_medications: true, show_summary: true, footer_note: '' };

export default function ReportSettings() {
    const [labs, setLabs] = useState([]);
    const [defaultLabId, setDefaultLabId] = useState('');
    const [s, setS] = useState(DEFAULTS);
    const [history, setHistory] = useState([]);
    const [reqNo, setReqNo] = useState('R10056935');
    const [busy, setBusy] = useState(false);
    const load = () => getReportSettings().then((res) => {
        if (res && res.success) {
            setLabs(res.data.labs || []); setDefaultLabId(String(res.data.defaultLabId || ''));
            setS({ ...DEFAULTS, ...(res.data.settings || {}) }); setHistory(res.data.history || []);
        } else toastr.error((res && res.message) || 'Could not load report settings');
    });
    useEffect(() => { load(); }, []);
    const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
    const save = (settings, msg) => saveReportSettings({ settings }).then((res) => {
        if (res && res.success) { toastr.success(msg || 'Saved'); load(); } else toastr.error((res && res.message) || 'Save failed');
    });
    const preview = () => {
        setBusy(true);
        previewReport({ lab_order_id: reqNo.trim() }).then((res) => {
            setBusy(false);
            if (res && res.success) window.open(`${serviceConstants.API_HOST_NAME}/files/resultPDF/${res.data.pdf_path}?${Date.now()}&token=${getUserAuthToken()}`, '_blank');
            else toastr.error((res && res.message) || 'Preview failed');
        }).catch(() => { setBusy(false); toastr.error('Preview failed'); });
    };
    return (
        <div style={{ width: '100%', flex: 1 }}>
            <PageHeader section="Administration" title="Report Settings" />
            <div className="clinic-contain bl-page" style={{ padding: 20 }}>
            <p className="text-muted">Customize the patient result report without code. Blank fields keep the standard report. Changes apply to newly generated PDFs.</p>
            <div className="row">
                <div className="col-12 col-lg-7">
                    <div className="card mb-3"><div className="card-body">
                        <h5>Lab header</h5>
                        <Form.Group className="mb-3">
                            <Form.Label>Lab shown on reports</Form.Label>
                            <div className="d-flex" style={{ gap: 8 }}>
                                <select className="form-control" value={defaultLabId} onChange={(e) => setDefaultLabId(e.target.value)}>
                                    {labs.map((l) => <option key={String(l._id)} value={String(l._id)}>{l.name}{l.isDefault ? ' (current)' : ''}</option>)}
                                </select>
                                <Button variant="outline-primary" style={{ whiteSpace: 'nowrap' }} onClick={() => setDefaultLab({ labId: defaultLabId }).then((r) => {
                                    if (r && r.success) { toastr.success('Default lab updated'); load(); } else toastr.error((r && r.message) || 'Failed');
                                })}>Make default</Button>
                            </div>
                            <Form.Text className="text-muted">Lab name, logo, address, director and disclaimer are edited in Lab PDF Management.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Report title</Form.Label>
                            <Form.Control value={s.report_title || ''} placeholder="Laboratory Result Report" onChange={(e) => set('report_title', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Accent color (lab name and section lines)</Form.Label>
                            <Form.Control type="color" value={s.accent_color || '#17a2b8'} onChange={(e) => set('accent_color', e.target.value)} style={{ width: 80, padding: 2 }} />
                        </Form.Group>
                        <h5 className="mt-3">Sections</h5>
                        <Form.Check type="checkbox" id="rs-meds" label="Show Reported Prescribed Medications" checked={s.show_medications !== false} onChange={(e) => set('show_medications', e.target.checked)} />
                        <Form.Check type="checkbox" id="rs-sum" label="Show Result Summary" checked={s.show_summary !== false} onChange={(e) => set('show_summary', e.target.checked)} />
                        <h5 className="mt-3">Footer / interpretation note</h5>
                        <Form.Control as="textarea" rows={4} value={s.footer_note || ''} placeholder="e.g. Results should be interpreted together with clinical findings and the patient's reported medications." onChange={(e) => set('footer_note', e.target.value)} />
                        <div className="d-flex mt-3" style={{ gap: 8 }}>
                            <Button variant="primary" onClick={() => save(s, 'Report settings saved')}>Save settings</Button>
                            <Button variant="outline-secondary" onClick={() => { if (window.confirm('Reset to the standard report?')) save({}, 'Reset to standard report'); }}>Reset to standard</Button>
                        </div>
                    </div></div>
                </div>
                <div className="col-12 col-lg-5">
                    <div className="card mb-3"><div className="card-body">
                        <h5>Preview</h5>
                        <p className="text-muted" style={{ fontSize: 13 }}>Save first, then regenerate an order's report with the saved settings and open it.</p>
                        <div className="d-flex" style={{ gap: 8 }}>
                            <Form.Control value={reqNo} onChange={(e) => setReqNo(e.target.value)} placeholder="Requisition #, e.g. R10056935" />
                            <Button variant="success" disabled={busy || !reqNo.trim()} onClick={preview}>{busy ? 'Generating…' : 'Preview'}</Button>
                        </div>
                    </div></div>
                    <div className="card"><div className="card-body">
                        <h5>Change history</h5>
                        {history.length ? (
                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}><tbody>
                                {history.map((h, i) => (
                                    <tr key={i}>
                                        <td>{h.saved_at ? new Date(h.saved_at).toLocaleString() : ''}</td>
                                        <td>{h.saved_by}</td>
                                        <td className="text-right"><Button size="sm" variant="link" onClick={() => { if (window.confirm('Restore these settings?')) save(h.settings || {}, 'Settings restored'); }}>Restore</Button></td>
                                    </tr>
                                ))}
                            </tbody></table>
                        ) : <span className="text-muted">No changes yet</span>}
                    </div></div>
                </div>
            </div>
            </div>
        </div>
    );
}
